import { datadogLogs } from '@datadog/browser-logs';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import * as R from '@/Router/routes';
import useError from '@/hooks/useError';
import { getProcessEnvUrl } from '@/utils/getUrl';

export type DocumentUploadResponse = {
  id: number;
  filename: string;
  type: string;
  psdRun: boolean;
};

export type DocumentType =
  | 'frontSideDni'
  | 'backSideDni'
  | 'bankReceipt'
  | 'idCard'
  | 'passport'
  | 'gunLicense'
  | 'driversLicense'
  | 'photo'
  | 'otherDocument'
  | 'incomeDocument';

const useDocumentUpload = () => {
  const { showError } = useError();
  const navigate = useNavigate();

  const documentUpload = useCallback(
    async (
      file: File,
      type: DocumentType,
    ): Promise<DocumentUploadResponse | undefined> => {
      try {
        const formData = new FormData();

        formData.append('file', file);
        formData.append('type', type);
        const path = '/client/attachments';
        const url = getProcessEnvUrl('WEB_API_URL');
        // NOTE: When sending FormData, 'Content-Type' header must be undefined,
        // so that the browser would set the header automatically
        // TODO: utilize with fetchData method
        const response = await fetch(`${url}${path}`, {
          method: 'post',
          credentials: 'include',
          body: formData,
        });
        if (response.status === 401) {
          sessionStorage.removeItem('token');
          window.location.replace(`${R.LOGIN}?sessionExpired=true`);
          return;
        }

        if (!response.ok) {
          try {
            const data = await response.json();

            datadogLogs.logger.error(`request: ${path}`, {
              request_url: url,
              error: data,
            });
          } catch (exception) {
            datadogLogs.logger.error(`request: ${path} response parse`, {
              request_url: url,
              error: exception,
            });
          }

          throw new Error(response.status.toString());
        }

        const data = (await response.json()) as DocumentUploadResponse;
        if (data.psdRun) {
          navigate(R.PSD2_RECEIVING);
          throw new Error('Redirecting due to PSD2 error');
        }

        return data;
      } catch (e) {
        showError();
        throw e;
      }
    },
    [showError, navigate],
  );

  const sendAttachmentsToIncomeProof = useCallback(
    async (attachmentsId: number[]) => {
      const path = '/client/income-proof';
      const url = `${getProcessEnvUrl('WEB_API_URL')}${path}`;
      try {
        const response = await fetch(url, {
          method: 'post',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            attachmentsId,
          }),
        });

        if (!response.ok) {
          try {
            const data = await response.json();

            datadogLogs.logger.error(`request: ${path}`, {
              request_url: url,
              error: data,
            });
          } catch (exception) {
            datadogLogs.logger.error(`request: ${path} response parse`, {
              request_url: url,
              error: exception,
            });
          }

          throw new Error(response.status.toString());
        }

        const data = await response.json();
        if (data.psdRun) {
          navigate(R.PSD2_RECEIVING);
          throw new Error('Redirecting due to PSD2 error');
        }

        return response;
      } catch (e) {
        showError();
        throw e;
      }
    },
    [showError, navigate],
  );

  return useMemo(() => {
    return {
      documentUpload,
      sendAttachmentsToIncomeProof,
    };
  }, [documentUpload, sendAttachmentsToIncomeProof]);
};

export default useDocumentUpload;
